import {
    ActivityRecordType,
    activityRecordTypeLabels,
    CommunityOrgTypeSentinel,
    NoOrgTypeSentinel,
} from "@sp-crm/core";
import {
    ClearAllLink,
    OrganizationTypeIcon,
    ReferralTypeChecklistGroups,
} from "components/references/dashboard-reference/org-type-checklist";
import { ButtonSelect } from "components/ui/button-select";
import { Checkbox } from "components/ui/checkbox";
import { FilterChipRow } from "components/ui/filter-chip-row";
import { FilterPanelTrigger } from "components/ui/filter-panel-trigger";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { SearchInput } from "components/ui/search-input";
import React, { useState } from "react";
import {
    ActivitiesReportCountMode,
    ActivitiesReportFilters,
    orgTypeFilterApplies,
    withRecordTypes,
} from "./activities-report";

const noOrganizationTypeLabel = "No organization type";

const noOrganizationTypeHeading = "Not set";

const communityOrganizationTypeLabel = "Linked to a community";

const communityOrganizationTypeHeading = "Community";

const countModeOptions: { id: ActivitiesReportCountMode; label: string }[] = [
    { id: "category", label: "Category" },
    { id: "activityType", label: "Activity type" },
];

interface CountModeToggleProps {
    value: ActivitiesReportCountMode;
    onChange: (mode: ActivitiesReportCountMode) => void;
}

export const CountModeToggle: React.FC<CountModeToggleProps> = ({ value, onChange }) => (
    <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500">Count by:</span>
        <ButtonSelect
            options={countModeOptions}
            selectedIds={[value]}
            onToggle={id => onChange(id as ActivitiesReportCountMode)}
        />
    </div>
);

const toggled = <T,>(values: T[], value: T): T[] =>
    values.includes(value)
        ? values.filter(existing => existing !== value)
        : [...values, value];

const matchesFilterText = (label: string, filterText: string): boolean =>
    label.toLowerCase().includes(filterText.toLowerCase());

interface StandaloneOrgTypeChoiceProps {
    heading: string;
    label: string;
    organizationType: string;
    filterText: string;
    value: string[];
    onCheck: (organizationType: string) => void;
    onUncheck: (organizationType: string) => void;
}

const StandaloneOrgTypeChoice: React.FC<StandaloneOrgTypeChoiceProps> = props =>
    matchesFilterText(props.heading, props.filterText) ||
    matchesFilterText(props.label, props.filterText) ? (
        <li>
            <p className="font-bold">{props.heading}</p>
            <ul>
                <li>
                    <Checkbox
                        checked={props.value.includes(props.organizationType)}
                        onChange={e =>
                            e.target.checked
                                ? props.onCheck(props.organizationType)
                                : props.onUncheck(props.organizationType)
                        }
                        label={props.label}
                    />
                </li>
            </ul>
        </li>
    ) : null;

interface ActivityOrgTypeFilterProps {
    value: string[];
    onChange: (organizationTypes: string[]) => void;
}

const ActivityOrgTypeFilter: React.FC<ActivityOrgTypeFilterProps> = ({
    value,
    onChange,
}) => {
    const [showPanel, setShowPanel] = useState(false);
    const [filterText, setFilterText] = useState("");
    const withoutOrganizationType = (organizationType: string): string[] =>
        value.filter(existing => existing !== organizationType);
    const addOrganizationType = (organizationType: string) =>
        onChange([...withoutOrganizationType(organizationType), organizationType]);
    const removeOrganizationType = (organizationType: string) =>
        onChange(withoutOrganizationType(organizationType));

    return (
        <>
            <FilterPanelTrigger
                icon={<OrganizationTypeIcon />}
                label="Organization type"
                selectedCount={value.length}
                onOpen={() => setShowPanel(true)}
            />
            <ResponsivePanel
                headerText="Organization type"
                type={PanelType.medium}
                isOpen={showPanel}
                onDismiss={() => {
                    setShowPanel(false);
                    setFilterText("");
                }}>
                <div className="space-y-4">
                    <SearchInput
                        value={filterText}
                        onChange={e => setFilterText(e.target.value)}
                        onClear={() => setFilterText("")}
                    />
                    <ClearAllLink onClear={() => onChange([])} />
                    <ul>
                        <StandaloneOrgTypeChoice
                            heading={noOrganizationTypeHeading}
                            label={noOrganizationTypeLabel}
                            organizationType={NoOrgTypeSentinel}
                            filterText={filterText}
                            value={value}
                            onCheck={addOrganizationType}
                            onUncheck={removeOrganizationType}
                        />
                        <StandaloneOrgTypeChoice
                            heading={communityOrganizationTypeHeading}
                            label={communityOrganizationTypeLabel}
                            organizationType={CommunityOrgTypeSentinel}
                            filterText={filterText}
                            value={value}
                            onCheck={addOrganizationType}
                            onUncheck={removeOrganizationType}
                        />
                        <ReferralTypeChecklistGroups
                            filterText={filterText}
                            value={value}
                            onCheck={addOrganizationType}
                            onUncheck={removeOrganizationType}
                        />
                    </ul>
                </div>
            </ResponsivePanel>
        </>
    );
};

interface RecordTypeFilterProps {
    filters: ActivitiesReportFilters;
    availableRecordTypes: ActivityRecordType[];
    onChange: (filters: ActivitiesReportFilters) => void;
}

export const RecordTypeFilter: React.FC<RecordTypeFilterProps> = ({
    filters,
    availableRecordTypes,
    onChange,
}) => {
    const options = availableRecordTypes.map(recordType => ({
        id: recordType,
        label: activityRecordTypeLabels[recordType],
    }));
    const toggleRecordType = (recordType: ActivityRecordType) =>
        onChange(withRecordTypes(filters, toggled(filters.recordTypes, recordType)));

    return (
        <FilterChipRow
            label="Records:"
            options={options}
            selectedIds={filters.recordTypes}
            onToggle={id => toggleRecordType(id as ActivityRecordType)}
            onClear={() => onChange(withRecordTypes(filters, []))}
            trailing={
                orgTypeFilterApplies(filters.recordTypes) ? (
                    <ActivityOrgTypeFilter
                        value={filters.organizationTypes}
                        onChange={organizationTypes =>
                            onChange({ ...filters, organizationTypes })
                        }
                    />
                ) : null
            }
        />
    );
};
